// ---- Activity entries ----------------------------------------------------

export interface CardioEntry {
  activity: string
  datetime: string
  level?: string
  gradient?: string
  duration?: string
  calories?: string
  distance?: string
  notes?: string
}

export interface WeightsEntry {
  operation: string
  datetime: string
  kg?: string
  repetitions?: string
  intensity?: string // MET value for the calorie estimate
  duration?: string // HH:MM:SS — set time, drives the calorie estimate
  calories?: string // estimated burn (MET × weight × duration); editable
  notes?: string
}

export interface ExerciseEntry {
  exercise: string
  datetime: string
  intensity?: string
  distance?: string
  duration?: string
  calories?: string
  notes?: string
}

export type AnyEntry = CardioEntry | WeightsEntry | ExerciseEntry

/** UI-facing activity type. */
export type ActivityType = 'cardio' | 'weights' | 'exercise'

/** Storage list key (kept from the legacy data model). */
export type ListKey = 'activity' | 'operation' | 'exercise'

export interface Lists {
  activity: CardioEntry[]
  operation: WeightsEntry[]
  exercise: ExerciseEntry[]
}

/** An entry decorated with display metadata for cards/history. */
export type DisplayEntry = AnyEntry & {
  type: ActivityType
  id: number // index within its list
  displayName: string
}

// ---- Intake (Food & Drink — energy in) -----------------------------------

/**
 * The nutrient keys that scale and sum across items. Order is display order.
 * Units: energy kcal; protein/carbs/sugar/addedSugar/fat/satFat/mono/poly/trans/
 * omega3/fibre/alcohol in grams; sodium/cholesterol/potassium/calcium/iron/
 * magnesium/zinc/vitC/caffeine in mg; vitD/b12/folate/vitA in micrograms (µg).
 */
export const NUTRIENTS = [
  'calories',
  'protein',
  'carbs',
  'sugar',
  'addedSugar',
  'fibre',
  'fat',
  'saturatedFat',
  'monoFat',
  'polyFat',
  'transFat',
  'omega3',
  'cholesterol',
  'sodium',
  'potassium',
  'alcohol',
  'caffeine',
  'calcium',
  'iron',
  'magnesium',
  'zinc',
  'vitA',
  'vitC',
  'vitD',
  'b12',
  'folate',
] as const

export type Nutrient = (typeof NUTRIENTS)[number]

/** A nutrition panel — `calories` is guaranteed; every other nutrient optional. */
export type NutritionPanel = { calories: number } & Partial<Record<Nutrient, number>>

/** One component of an Intake (e.g. the fries within a meal). */
export type IntakeItem = NutritionPanel & {
  name: string
  quantity?: string // free-text portion, e.g. "regular" / "330ml"
}

/** A logged food/drink. Totals are always sum(items) × servings. */
export interface Intake {
  datetime: string
  query: string // what the user typed
  name: string // canonical name of the whole intake
  servings: number
  items: IntakeItem[]
  manual?: boolean // logged via the manual fallback (no Claude lookup)
}

// ---- User record ---------------------------------------------------------

export type PasswordMode = 'on_load' | 'after_timeout'

/** Self-reported activity level → TDEE multiplier (see utils/targets). */
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active'

/** Diet goal → calorie-target adjustment. */
export type Goal = 'lose' | 'maintain' | 'gain'

/** User data stored in Cloudflare KV (plaintext — no E2E encryption). */
export interface UserData {
  indexKey?: string
  name?: string
  email?: string
  weight: number
  gender: '' | 'male' | 'female'
  // Profile context for personalised targets (all optional)
  heightCm?: number
  dob?: string // ISO 'YYYY-MM-DD'
  activityLevel?: ActivityLevel
  goal?: Goal
  customCalories?: number // optional override of computed calorie target
  customProtein?: number // optional override of computed protein target (g)
  lists: Lists
  intake?: Intake[] // Food & Drink — energy in (kept separate from lists)
  // Optional auth fields (absent on legacy records)
  passwordHash?: string
  passwordSalt?: string
  passwordIterations?: number
  passwordMode?: PasswordMode
  timeoutMinutes?: number
  backupEmail?: string
}

/** localStorage cache adds a sync timestamp. */
export interface LocalStorageData extends UserData {
  lastSync?: number
}

// ---- API response shapes -------------------------------------------------

export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
}

export interface BackupInfo {
  timestamp: string
  date: string
  key: string
}

export interface BackupListResponse {
  success: boolean
  backups: BackupInfo[]
}

export interface PasswordVerifyResult {
  valid: boolean
}

export interface RecoveryResult {
  success: boolean
}
